import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { Modal } from '../components/Modal';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import ToggleSwitch from '../components/ToggleSwitch';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { usePagedRows } from '../hooks/usePagedRows';
import SearchInput from '../components/ui/SearchInput';
import { EmptyState, ErrorState } from '../components/ui/States';
import { FIELD as FIELD_CLASS } from '../components/ui/tokens';
import { get, patch, deleteRequest } from '../lib/apiClient';
import { resetVendorOptionsCache } from '../lib/vendorsApi';
import { deletePurchaseOrderErrorMessage } from '../lib/vendorPurchaseOrderApi';
import { normalizeErrorMessage } from '../utils/errorPresentation';
import { usePermission } from '../hooks/usePermission';
import { VendorItemMemoryPanel } from '../components/VendorItemMemoryPanel';
import { creditDaysOnFile } from '../utils/paymentTerms';
import { VendorForm, type VendorFormValues } from '../features/vendors/VendorForm';

const VENDORS_PAGE_SIZE = 25;

/* ------------------------------
   Types
------------------------------ */
type Vendor = {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  priceListUrl?: string;
  creditDays?: number;
  code?: string;
  hideVesselOnPo?: boolean;
  createdAt?: string;
  // D35 (VI-1): created by the invoice processor, not yet looked at by a person.
  needsReview?: boolean;
};

/** A stored "+<dial code> <number>" phone as the form's two fields: the form's Edit start values. */
function splitStoredPhone(raw: string | undefined | null): { countryCode: string; phone: string } {
  const phone = raw || '';
  const m = phone.match(/^(\+\d{1,4})\s*(.*)$/);
  return m ? { countryCode: m[1], phone: m[2] } : { countryCode: '+1868', phone };
}

/* ------------------------------
   Component
------------------------------ */
export const VendorsPage: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const paged = usePagedRows(vendors, VENDORS_PAGE_SIZE);
  const location = useLocation();
  const navigate = useNavigate();

  // MC-6: everything the item memory holds for one vendor, reached from the
  // vendor itself. Seeing it follows invoices, which is what the server gates
  // the list on; changing or forgetting it needs Master Catalogue rights (D29).
  const invoicePerms = usePermission('invoices');
  const catalogueRights = usePermission('products');
  // USC-BUG-0031: the panel also carries the supplier's Payment Terms, which are
  // stored on the vendor and gated on VENDORS rights, not the catalogue's. The
  // Vendor-Invoices door passed these from the start and this one did not, so
  // the same vendor showed the box there and only the product table here.
  const vendorRights = usePermission('vendors');
  const [memoryFor, setMemoryFor] = useState<Vendor | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  // What the form opens with; `formKey` makes it start afresh each time it is opened (an edit, then another).
  const [formInitial, setFormInitial] = useState<Partial<VendorFormValues>>({});
  const [formKey, setFormKey] = useState(0);
  const showForm = useCallback((initial: Partial<VendorFormValues>, vendor: Vendor | null) => {
    setEditingVendor(vendor);
    setFormInitial(initial);
    setFormKey((k) => k + 1);
    setIsModalOpen(true);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFromUrl, setSelectedFromUrl] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchVendors = useCallback(async (overrideSearch?: string) => {
    try {
      const term = (overrideSearch ?? searchTerm).trim();
      const query = term ? `?search=${encodeURIComponent(term)}` : '';
      const res: any = await get(`/vendors${query}`);
      setVendors(res?.data ?? []);
      setActionError(null);
      setLoadError(null);
      setSuccessMsg(null);
    } catch (err: any) {
      // This used to be a bare `finally {}`: a failed load rejected into nowhere and
      // the screen showed an empty vendor list, which reads as "you have no vendors".
      setLoadError(normalizeErrorMessage(err));
    }
  }, [searchTerm]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const searchParam = qs.get('search') || '';
    const selectedParam = qs.get('selected');
    if (searchParam) setSearchTerm(searchParam);
    if (selectedParam) setSelectedFromUrl(selectedParam);
    fetchVendors(searchParam || undefined);
  }, [location.search, fetchVendors]);

  const openCreate = () => showForm({}, null);

  const openEdit = useCallback((v: Vendor) => {
    const { countryCode, phone } = splitStoredPhone(v.phone);

    showForm(
      {
        name: v.name,
        email: v.email,
        countryCode,
        phone,
        address: v.address || '',
        creditDays: String(creditDaysOnFile(v.creditDays) ?? ''),
        code: v.code || '',
        isActive: v.isActive,
        priceListUrl: v.priceListUrl || '',
        hideVesselOnPo: v.hideVesselOnPo === true,
      },
      v,
    );
  }, [showForm]);

  useEffect(() => {
    if (!selectedFromUrl) return;
    const match = vendors.find((v) => v._id === selectedFromUrl);
    if (match) {
      openEdit(match);
      setSelectedFromUrl(null);
    }
  }, [selectedFromUrl, vendors, openEdit]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVendor(null);
  };

  // The form saved (create or edit).
  const onSaved = async () => {
    await fetchVendors();
    closeModal();
    setSuccessMsg(editingVendor ? 'Vendor updated successfully.' : 'Vendor created successfully.');
  };

  const toggleActive = async (v: Vendor) => {
    try {
      setActionError(null);
      setSuccessMsg(null);
      await patch(`/vendors/${v._id}/toggle-active`, {});
      resetVendorOptionsCache();
      fetchVendors();
    } catch (e: any) {
      setActionError(normalizeErrorMessage(e));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    setDeleteError(null);
    try {
      await deleteRequest(`/vendors/${deleteTarget._id}`);
      resetVendorOptionsCache();
      setDeleteTarget(null);
      setDeleteConfirmText('');
      setSuccessMsg('Vendor deleted successfully.');
      fetchVendors();
    } catch (e: any) {
      // D81: a vendor whose PO the supplier has, or an invoice points at, is kept (by code).
      setDeleteError(
        e?.code === 'VENDOR_PO_DELETE_LOCKED'
          ? `This vendor cannot be deleted. ${deletePurchaseOrderErrorMessage(e, '')}`
          : normalizeErrorMessage(e) || 'Unable to delete vendor'
      );
    }
  };

  const VENDORS_INFO_CONTENT = (
    <div className="flex flex-col gap-3">
      <p>This section shows a list of all your vendors.</p>
      <p>It includes vendor name, contact details, and address.</p>
      <p className="font-medium">From here, you can:</p>
      <ul className="list-disc list-inside space-y-1 pl-1">
        <li>Add a new vendor</li>
        <li>Edit existing vendor details</li>
        <li>Delete a vendor</li>
        <li>See, search and correct what item memory learned from a vendor's invoices</li>
      </ul>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor List</h1>
            <ModuleInfoButton content={VENDORS_INFO_CONTENT} ariaLabel="Vendor list information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search here"
              className="w-56"
            />
            {/* Search is an explicit action here, not debounced: the list is server-filtered. */}
            <Button onClick={() => fetchVendors()}>Search</Button>
            <Button
              variant="primary"
              onClick={openCreate}
              icon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Add vendor
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {actionError && (
            <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-200">
              {actionError}
            </div>
          )}
          {successMsg && (
            <div className="px-4 py-2 text-xs text-emerald-700 bg-emerald-50 border-b border-emerald-200">
              {successMsg}
            </div>
          )}
          {/* Fixed layout: the text columns share the width equally, so the gaps between columns stay the same. */}
          <table className="data-table table-fixed break-words">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Vendor name</th>
                <th>Email</th>
                <th className="w-44">Contact</th>
                <th>Address</th>
                <th className="w-28 whitespace-nowrap">Credit days</th>
                {/* Wide enough for every row action (view, Item memory, edit, active, delete): narrower, the
                    right-aligned buttons spilled left over the Credit days text. */}
                <th className="w-72">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.visible.map((v, i) => (
                <tr key={v._id}>
                  <td className="tabular-nums text-slate-500">{paged.first + i}</td>
                  <td className="font-medium text-slate-900">
                    {v.name}
                    {v.needsReview ? (
                      <span
                        className="ml-2 whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700"
                        title="Created automatically from an uploaded invoice because no registered supplier matched. Check the details, then Edit and Save to clear this."
                      >
                        Auto-created, review
                      </span>
                    ) : null}
                  </td>
                  <td>{v.email}</td>
                  <td className="whitespace-nowrap">{v.phone || '-'}</td>
                  <td>{v.address || '-'}</td>
                  <td className="whitespace-nowrap">
                    {creditDaysOnFile(v.creditDays) === null ? 'Not on file' : `${creditDaysOnFile(v.creditDays)} days`}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/vendors/${v._id}`)}
                        aria-label={`View details for ${v.name}`}
                        title="View vendor detail: balance, invoices and purchase orders"
                        icon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
                      />

                      {invoicePerms.canView && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setMemoryFor(v)}
                          aria-label={`Item memory for ${v.name}`}
                          title="What past invoices from this vendor taught the system, with search"
                        >
                          Item memory
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(v)}
                        aria-label={`Edit ${v.name}`}
                        title="Edit"
                        icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                      />

                      <ToggleSwitch
                        checked={v.isActive}
                        onChange={() => toggleActive(v)}
                        label={v.isActive ? `Deactivate ${v.name}` : `Activate ${v.name}`}
                      />

                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setDeleteTarget(v);
                          setDeleteError(null);
                          setDeleteConfirmText('');
                        }}
                        aria-label={`Delete ${v.name}`}
                        title="Delete"
                        icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {/* A refused or failed search is not the same answer as "no vendors match". */}
              {loadError && (
                <tr>
                  <td colSpan={7} className="px-4 py-6">
                    <ErrorState message={loadError} onRetry={() => fetchVendors()} />
                  </td>
                </tr>
              )}

              {!loadError && vendors.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6">
                    <EmptyState
                      title="No vendors found"
                      detail={
                        searchTerm.trim()
                          ? 'No vendor matches that search. Clear it to see the full list.'
                          : 'Add a vendor to quote and purchase against it.'
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onPageChange={paged.setPage} noun="vendors" />
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={isModalOpen} title={editingVendor ? 'Edit vendor' : 'Add new vendor'} onClose={closeModal}>
        <VendorForm
          key={formKey}
          initial={formInitial}
          editingId={editingVendor?._id}
          onSaved={onSaved}
          onCancel={closeModal}
        />
      </Modal>

      {/* Delete Confirmation, deliberately NOT useConfirm: deleting a vendor takes its
          associated records with it, so this one asks the word to be typed out. */}
      <Modal
        isOpen={!!deleteTarget}
        title="Delete vendor"
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmText('');
          setDeleteError(null);
        }}
        maxWidthClass="max-w-md"
      >
        <p className="text-sm text-slate-700">
          Deleting the vendor will delete the vendor along with its associated records.
        </p>
        <p className="text-sm text-slate-700 mt-2">
          Type <span className="font-semibold">DELETE</span> to confirm.
        </p>
        <input
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder="Type DELETE"
          className={`${FIELD_CLASS} mt-3 h-10 ${deleteError ? 'border-red-300' : 'border-slate-200'}`}
        />
        {deleteError && <p className="text-sm text-red-600 mt-2">{deleteError}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      <VendorItemMemoryPanel
        isOpen={!!memoryFor}
        onClose={() => setMemoryFor(null)}
        vendorId={memoryFor?._id ?? null}
        vendorName={memoryFor?.name}
        canEdit={catalogueRights.canEdit}
        canDelete={catalogueRights.canDelete}
        termsRights={vendorRights}
      />
    </div>
  );
};
